import { useState, useMemo, useRef } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Upload, Image, Paintbrush, Check, AlertTriangle, ChevronRight } from "lucide-react";
import { SitePageShell } from "@/components/SiteHeader";

/* ============================================================
 * Constants
 * ============================================================ */
const SIZES = ["A4", "A3", "A2"] as const;
type Size = typeof SIZES[number];

const RESOLUTION_REQUIREMENTS = {
  photo: { A4: 2000, A3: 3000, A2: 4500 },
  oil_painting: { A4: 1800, A3: 2500, A2: 3500 },
} as const;

function getResolutionStatus(
  longEdge: number,
  serviceType: "photo" | "oil_painting",
  size: Size,
): "good" | "fair" | "low" {
  const req = RESOLUTION_REQUIREMENTS[serviceType][size];
  if (longEdge >= req) return "good";
  if (longEdge >= req * 0.7) return "fair";
  return "low";
}

const RESOLUTION_LABELS = {
  good: { text: "출력 적합", color: "text-green-600", icon: "✓" },
  fair: { text: "출력 가능", color: "text-yellow-600", icon: "△" },
  low: { text: "작은 사이즈 권장", color: "text-red-600", icon: "!" },
} as const;

/* ============================================================
 * Main Component
 * ============================================================ */
export default function OrderPage() {
  const { isAuthenticated, loading, user } = useAuth();

  // Steps: 1=upload, 2=service, 3=size, 4=orientation, 5=preview, 6=checkout
  const [step, setStep] = useState(1);

  // Upload state
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [imageMime, setImageMime] = useState("image/jpeg");
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [uploadedFileKey, setUploadedFileKey] = useState<string | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Selection state
  const [serviceType, setServiceType] = useState<"photo" | "oil_painting" | null>(null);
  const [size, setSize] = useState<Size | null>(null);
  const [orientation, setOrientation] = useState<"landscape" | "portrait" | null>(null);

  // Derived
  const longEdge = Math.max(imageWidth, imageHeight);
  const resolutionStatus = useMemo(() => {
    if (!serviceType || !size) return null;
    return getResolutionStatus(longEdge, serviceType, size);
  }, [longEdge, serviceType, size]);

  const productCode = useMemo(() => {
    if (!serviceType || !size) return null;
    const prefix = serviceType === "photo" ? "photo" : "oil";
    return `${prefix}_${size.toLowerCase()}`;
  }, [serviceType, size]);

  // Upload mutation
  const uploadRef = trpc.images.uploadReference.useMutation({
    onSuccess: (data) => {
      setUploadedFileKey(data.fileKey);
      setUploadedUrl(data.url);
    },
  });

  // Product price query
  const priceQuery = trpc.orders.estimateByCode.useQuery(
    { productCode: productCode ?? "" },
    { enabled: !!productCode },
  );

  const price = priceQuery.data?.price ?? 0;

  /* ---- File handling ---- */
  const handleFile = (file: File) => {
    if (file.size > 30 * 1024 * 1024) {
      toast.error("파일 크기는 30MB 이하여야 합니다.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setImagePreview(dataUrl);
      setImageMime(file.type || "image/jpeg");

      // Get dimensions
      const img = new window.Image();
      img.onload = () => {
        setImageWidth(img.naturalWidth);
        setImageHeight(img.naturalHeight);
      };
      img.src = dataUrl;

      // Extract base64
      const base64 = dataUrl.split(",")[1];
      setImageBase64(base64);
      setStep(2);
    };
    reader.readAsDataURL(file);
  };

  /* ---- Upload to server ---- */
  const ensureUploaded = async () => {
    if (uploadedFileKey && uploadedUrl) return { fileKey: uploadedFileKey, url: uploadedUrl };
    if (!imageBase64) throw new Error("No image");
    const res = await uploadRef.mutateAsync({ base64: imageBase64, mimeType: imageMime });
    setUploadedFileKey(res.fileKey);
    setUploadedUrl(res.url);
    return { fileKey: res.fileKey, url: res.url };
  };

  /* ---- Auto-detect orientation ---- */
  const autoOrientation = imageWidth > imageHeight ? "landscape" : "portrait";

  /* ---- Render ---- */
  if (loading) {
    return <SitePageShell><div className="container py-20 text-center text-muted-foreground">로딩 중...</div></SitePageShell>;
  }
  if (!isAuthenticated) {
    return (
      <SitePageShell>
        <div className="container py-20 text-center">
          <p className="text-muted-foreground mb-4">주문하려면 로그인이 필요합니다.</p>
          <a href={getLoginUrl("/order")} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors">
            로그인
          </a>
        </div>
      </SitePageShell>
    );
  }

  return (
    <SitePageShell>
      <div className="container max-w-2xl mx-auto py-6 md:py-10 px-4">
        {/* Progress */}
        <div className="flex items-center gap-1 mb-8 text-xs text-muted-foreground">
          {["사진 업로드", "출력 방식", "사이즈", "방향", "확인"].map((label, i) => (
            <div key={label} className="flex items-center gap-1">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                step > i + 1 ? "bg-primary text-primary-foreground" :
                step === i + 1 ? "bg-primary/20 text-primary border border-primary" :
                "bg-muted text-muted-foreground"
              }`}>{step > i + 1 ? <Check className="w-3 h-3" /> : i + 1}</span>
              <span className={step === i + 1 ? "font-medium text-foreground" : ""}>{label}</span>
              {i < 4 && <ChevronRight className="w-3 h-3 opacity-40" />}
            </div>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <Card>
            <CardContent className="p-6 md:p-8 text-center">
              <h2 className="text-xl font-serif font-semibold mb-2">사진을 선택해주세요</h2>
              <p className="text-sm text-muted-foreground mb-6">아이, 반려동물, 가족 사진을 추천합니다.</p>
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-xl p-10 cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all">
                <Upload className="w-10 h-10 mb-3 text-muted-foreground/60" />
                <span className="font-medium text-sm">사진 선택</span>
                <span className="text-xs text-muted-foreground mt-1">JPG, PNG (최대 30MB)</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                />
              </label>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Service Type */}
        {step === 2 && (
          <Card>
            <CardContent className="p-6 md:p-8">
              <h2 className="text-xl font-serif font-semibold mb-2">출력 방식을 선택해주세요</h2>
              <p className="text-sm text-muted-foreground mb-6">사진을 어떻게 출력할까요?</p>

              {imagePreview && (
                <div className="mb-6 flex justify-center">
                  <img src={imagePreview} alt="업로드 사진" className="max-h-40 rounded-lg border border-border/60 object-contain" />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  onClick={() => { setServiceType("photo"); setStep(3); }}
                  className="flex flex-col items-center p-6 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all text-center"
                >
                  <Image className="w-8 h-8 mb-3 text-blue-600" />
                  <span className="font-semibold text-base">사진 그대로 출력</span>
                  <span className="text-xs text-muted-foreground mt-1.5">원본 그대로 고품질 인화</span>
                  <span className="font-serif tabular-nums text-sm font-medium text-primary mt-3">29,000원~</span>
                </button>
                <button
                  onClick={() => { setServiceType("oil_painting"); setStep(3); }}
                  className="flex flex-col items-center p-6 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all text-center"
                >
                  <Paintbrush className="w-8 h-8 mb-3 text-amber-600" />
                  <span className="font-semibold text-base">유화 작품으로 만들기</span>
                  <span className="text-xs text-muted-foreground mt-1.5">원본 보존 + 유화 질감 적용</span>
                  <span className="font-serif tabular-nums text-sm font-medium text-primary mt-3">39,000원~</span>
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Size */}
        {step === 3 && (
          <Card>
            <CardContent className="p-6 md:p-8">
              <h2 className="text-xl font-serif font-semibold mb-2">사이즈를 선택해주세요</h2>
              <p className="text-sm text-muted-foreground mb-6">
                {serviceType === "photo" ? "실사출력" : "유화출력"} 사이즈
              </p>

              <div className="space-y-3">
                {SIZES.map((s) => {
                  const code = `${serviceType === "photo" ? "photo" : "oil"}_${s.toLowerCase()}`;
                  const prices: Record<string, number> = {
                    photo_a4: 29000, photo_a3: 49000, photo_a2: 79000,
                    oil_a4: 39000, oil_a3: 69000, oil_a2: 109000,
                  };
                  const p = prices[code] ?? 0;
                  const res = getResolutionStatus(longEdge, serviceType!, s);
                  const resLabel = RESOLUTION_LABELS[res];

                  return (
                    <button
                      key={s}
                      onClick={() => { setSize(s); setOrientation(autoOrientation); setStep(4); }}
                      className="w-full flex items-center justify-between p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold">{s}</span>
                        <span className={`text-xs ${resLabel.color}`}>{resLabel.icon} {resLabel.text}</span>
                      </div>
                      <span className="font-serif tabular-nums font-medium">{p.toLocaleString()}원</span>
                    </button>
                  );
                })}
              </div>

              {resolutionStatus === "low" && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-red-700">이미지 해상도가 낮아 큰 사이즈에서 화질이 떨어질 수 있습니다.</p>
                </div>
              )}

              <Button variant="ghost" className="mt-4" onClick={() => setStep(2)}>← 이전</Button>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Orientation */}
        {step === 4 && (
          <Card>
            <CardContent className="p-6 md:p-8">
              <h2 className="text-xl font-serif font-semibold mb-2">방향을 선택해주세요</h2>
              <p className="text-sm text-muted-foreground mb-6">{size} {serviceType === "photo" ? "실사출력" : "유화출력"}</p>

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => { setOrientation("portrait"); setStep(5); }}
                  className={`flex flex-col items-center p-5 rounded-xl border-2 transition-all ${
                    orientation === "portrait" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                  }`}
                >
                  <div className="w-12 h-16 border-2 border-current rounded mb-3" />
                  <span className="font-medium text-sm">세로형</span>
                </button>
                <button
                  onClick={() => { setOrientation("landscape"); setStep(5); }}
                  className={`flex flex-col items-center p-5 rounded-xl border-2 transition-all ${
                    orientation === "landscape" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                  }`}
                >
                  <div className="w-16 h-12 border-2 border-current rounded mb-3" />
                  <span className="font-medium text-sm">가로형</span>
                </button>
              </div>

              <Button variant="ghost" className="mt-4" onClick={() => setStep(3)}>← 이전</Button>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Preview & Confirm */}
        {step === 5 && (
          <Card>
            <CardContent className="p-6 md:p-8">
              <h2 className="text-xl font-serif font-semibold mb-2">주문 확인</h2>

              {/* Preview with aspect ratio */}
              {imagePreview && (
                <div className="mb-6 flex justify-center">
                  <div className={`relative border border-border rounded-lg overflow-hidden ${
                    orientation === "landscape" ? "max-w-sm aspect-[4/3]" : "max-w-[200px] aspect-[3/4]"
                  }`}>
                    <img
                      src={imagePreview}
                      alt="미리보기"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}

              {/* Order summary */}
              <div className="space-y-2 mb-6 p-4 bg-muted/50 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">출력 방식</span>
                  <span className="font-medium">{serviceType === "photo" ? "실사출력" : "유화출력"}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">사이즈</span>
                  <span className="font-medium">{size}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">방향</span>
                  <span className="font-medium">{orientation === "landscape" ? "가로형" : "세로형"}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">해상도</span>
                  <span className={`font-medium ${RESOLUTION_LABELS[resolutionStatus ?? "good"].color}`}>
                    {RESOLUTION_LABELS[resolutionStatus ?? "good"].text} ({longEdge}px)
                  </span>
                </div>
                <div className="border-t border-border pt-2 mt-2 flex justify-between items-baseline">
                  <span className="font-medium">결제 금액</span>
                  <span className="font-serif text-xl tabular-nums text-primary">{price.toLocaleString()}원</span>
                </div>
              </div>

              {serviceType === "oil_painting" && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-800">
                    아르가 출력에 맞게 준비해드릴게요. 원본의 얼굴, 표정, 구도를 최대한 유지하며 유화 질감을 적용합니다.
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(4)} className="flex-1">
                  ← 이전
                </Button>
                <Button
                  size="lg"
                  onClick={() => {
                    // Navigate to checkout with order params
                    const params = new URLSearchParams({
                      productCode: productCode!,
                      orientation: orientation!,
                      resolutionStatus: resolutionStatus ?? "good",
                      serviceType: serviceType!,
                    });
                    // Upload first, then navigate
                    ensureUploaded().then(({ fileKey, url }) => {
                      params.set("fileKey", fileKey);
                      params.set("imageUrl", url);
                      window.location.href = `/checkout-v1?${params.toString()}`;
                    }).catch(() => toast.error("업로드에 실패했습니다."));
                  }}
                  className="flex-1 font-semibold"
                  disabled={uploadRef.isPending}
                >
                  {uploadRef.isPending ? "업로드 중..." : "결제하기"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </SitePageShell>
  );
}
